import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    Signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Observable, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";

import { Shop } from "@hermes/api-model-shop";
import { LOCALE } from "@hermes/app-core";
import { SectionName, ShopInfoEvent } from "@hermes/fragments/shop";
import { Locale } from "@hermes/locale";
import {
    getCoordinatesBasedOnLocale,
    ShopLocatorService,
} from "@hermes/shop-utils";
import {
    APPOINTMENT_IN_STORE,
    FeatureFlagFacade,
} from "@hermes/states/flipper";
import {
    getHoveredAndVisibleShopsIds,
    HoveredShopEventOrigin,
    ShopLocatorExtraInfos,
    ShopLocatorFacade,
    ShopsStateData,
} from "@hermes/states/shop-locator";
import { AnalyticsService } from "@hermes/fragments/analytics";

import { PromotionImpressionShopLocator } from "../../events/promotion-impression-shop-locator.event";

export const DEFAULT_ITEMS_LIMIT = 125;

@Component({
    selector: "h-shop-locator-shops-list",
    templateUrl: "./shop-locator-shops-list.component.html",
    styleUrls: ["./shop-locator-shops-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ShopLocatorShopsListComponent implements OnInit, OnDestroy {
    @Output()
    public shopInfoEvents = new EventEmitter<{
        event: ShopInfoEvent;
        shop: Shop;
        shopIndex: number;
    }>();

    public originList = HoveredShopEventOrigin.List;

    public itemsLimitIsReached$!: Observable<boolean>;

    public sectionsToDisplay: Signal<Set<SectionName>> = computed(() => {
        const sections = new Set<SectionName>([
            "streetAddress1",
            "phoneNumber",
        ]);
        if (this.shouldDisplayAppointmentButton()) {
            sections.add("appointmentInStore");
        }
        return sections;
    });

    public mainStream$: Observable<{
        shops: ShopsStateData;
        visibleShopIds: string[];
        displayWarningMessage: boolean;
        extraInfos: ShopLocatorExtraInfos;
    }> = this.shopLocatorFacade.mainStreams$;

    private shouldDisplayAppointmentButton = toSignal(
        this.featureFlagFacade.isActivated(APPOINTMENT_IN_STORE),
    );

    private subscription: Subscription = new Subscription();

    constructor(
        public shopLocatorService: ShopLocatorService,
        public shopLocatorFacade: ShopLocatorFacade,
        public featureFlagFacade: FeatureFlagFacade,
        private analyticsService: AnalyticsService,
        @Inject(LOCALE) private readonly locale: Locale,
    ) {}

    public ngOnInit(): void {
        this.itemsLimitIsReached$ = this.shopLocatorFacade.visibleShopIds$.pipe(
            map(
                (visibleShopIds) => visibleShopIds.length > DEFAULT_ITEMS_LIMIT,
            ),
        );

        this.subscription.add(
            getHoveredAndVisibleShopsIds(
                this.shopLocatorFacade.visibleShopIds$,
                this.shopLocatorFacade.hoveredShop$,
            ).subscribe(([hoveredShopId, shopIds]) => {
                this.shopLocatorService.scrollToShowShop(
                    hoveredShopId,
                    shopIds,
                );
            }),
        );

        this.initAnalyticsEventOnSearch();
    }

    public setHoveredShop(shopId: string): void {
        this.subscription.add(
            this.mainStream$.pipe(take(1)).subscribe((stream) => {
                const coord = getCoordinatesBasedOnLocale(
                    stream.shops[shopId],
                    this.locale,
                );
                this.shopLocatorFacade.setHoveredShop(
                    shopId,
                    coord,
                    this.originList,
                );
            }),
        );
    }

    public shopInfoEvent(
        event: ShopInfoEvent,
        shopId: string,
        shopIndex: number,
    ): void {
        this.subscription.add(
            this.mainStream$
                .pipe(
                    map((stream) => stream.shops[shopId]),
                    take(1),
                )
                .subscribe((shop) => {
                    this.shopInfoEvents.emit({ event, shop, shopIndex });
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * On each new search, we have to send some analytics event.
     */
    private initAnalyticsEventOnSearch() {
        this.subscription.add(
            this.shopLocatorFacade.shopsDisplayedAfterSearch$.subscribe(
                ([, visibleShopIds, shops]) => {
                    const event = new PromotionImpressionShopLocator({
                        shops: visibleShopIds.map((shopId, index) => ({
                            name: shops[shopId].shortTitle,
                            position: index + 1,
                            id: shopId,
                        })),
                    });
                    this.analyticsService.sendData(event);
                },
            ),
        );
    }
}
